import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { AppType } from '@/models/enums/AppType';
import { type Playlist, PROMOTION_APP_TYPES, toggleApp } from '@/models/media-network/Playlist';
import { appTypeLabel } from '@/models/media-network/appLabels';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

interface Props {
    open: boolean;
    brandId: string;
    playlist: Playlist | null; // null = create
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const PlaylistDialog = ({ open, brandId, playlist, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    // No default app. Defaulting to Dual Display meant a playlist could be created targeting it purely
    // because nobody looked at the field, which is worse than being made to choose.
    const [apps, setApps] = useState<AppType[]>([]);
    // The requirement is only worth shouting about once someone has actually engaged with the field.
    const [appsTouched, setAppsTouched] = useState(false);
    const [enabled, setEnabled] = useState(true);
    // Empty string means "inherit" — distinct from 0, which isn't a legal interval.
    const [interval, setInterval] = useState<string>('');

    useEffect(() => {
        if (!open) return;
        setName(playlist?.name ?? '');
        setDescription(playlist?.description ?? '');
        setApps(playlist?.apps ?? []);
        setAppsTouched(false);
        setEnabled(playlist?.enabled ?? true);
        setInterval(playlist?.slideshowIntervalSeconds != null ? String(playlist.slideshowIntervalSeconds) : '');
    }, [open, playlist]);

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            const body = {
                name: name.trim(),
                description: description.trim() || null,
                apps,
                enabled,
                slideshowIntervalSeconds: interval.trim() === '' ? null : Number(interval),
            };

            if (playlist) {
                await mediaNetworkApi.update(playlist.id, body);
            } else {
                await mediaNetworkApi.create({ ...body, brandId });
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistSaved });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.list(brandId) });
            if (playlist) await qc.invalidateQueries({ queryKey: mediaNetworkKeys.detail(playlist.id) });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.playlistSaveError }),
    });

    const intervalValid = interval.trim() === '' || Number(interval) >= 1;
    const canSave = !!name.trim() && apps.length > 0 && intervalValid;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{playlist ? strings.playlistEdit : strings.playlistNew}</DialogTitle>
            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label={strings.name}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        autoFocus
                    />

                    <TextField
                        label={strings.description}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />

                    <div>
                        <FormLabel component="legend">{strings.playlistShowsOn}</FormLabel>
                        {/* Only the apps that can play a playlist are offered. A playlist targeting none of
                            them never resolves anywhere, so at least one is required. */}
                        <FormGroup row>
                            {PROMOTION_APP_TYPES.map((app) => (
                                <FormControlLabel
                                    key={app}
                                    control={
                                        <Checkbox
                                            checked={apps.includes(app)}
                                            onChange={(e) => {
                                                setAppsTouched(true);
                                                setApps(toggleApp(apps, app, e.target.checked));
                                            }}
                                        />
                                    }
                                    label={appTypeLabel(app)}
                                />
                            ))}
                        </FormGroup>
                        {/* Always says what's required — a Save button that's disabled with no explanation is
                            its own bug. Turns red only once they've touched the field and left it empty. */}
                        {apps.length === 0 && (
                            <Typography variant="caption" color={appsTouched ? 'error' : 'text.secondary'}>
                                {strings.playlistShowsOnRequired}
                            </Typography>
                        )}
                    </div>

                    <TextField
                        label={strings.playlistIntervalOverride}
                        type="number"
                        value={interval}
                        onChange={(e) => setInterval(e.target.value)}
                        helperText={intervalValid ? strings.playlistIntervalOverrideHelp : strings.playlistIntervalInvalid}
                        error={!intervalValid}
                        slotProps={{ htmlInput: { min: 1 } }}
                        fullWidth
                    />

                    <FormControlLabel
                        control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                        label={strings.enabled}
                    />
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button variant="contained" loading={isPending} disabled={!canSave || isPending} onClick={() => save()}>
                    {strings.save}
                </Button>}
            </DialogActions>
        </Dialog>
    );
};

export default PlaylistDialog;
